import { useQuery } from '@tanstack/react-query'
import type { TransformedOrderItemFilters } from '@/components/dashboard/hooks/use-transformed-order-item-filters'
import { type OrderItemsStatusFilter, useOrderItemsService } from '@/flight-deck-api/services/order-items.service'
import { useAuthenticatedSession } from '@/providers/session'

interface UseOrderItemsSummaryParams {
  status: OrderItemsStatusFilter
  search?: string
  filters?: TransformedOrderItemFilters
}

/**
 * Hook to fetch order items summary for the dashboard
 * Provides status summary (counts) and order item summary (aggregated totals)
 * Queries are cached and deduped by React Query
 */
export const useOrderItemsSummary = ({ status, search, filters }: UseOrderItemsSummaryParams) => {
  const { session } = useAuthenticatedSession()
  const { getOrderItemsStatusSummary, getOrderItemSummary } = useOrderItemsService()

  // Fetch status summary
  const statusSummaryQuery = useQuery({
    queryKey: ['orderItemsStatusSummary', session.advertiser.id, search],
    queryFn: async () =>
      // biome-ignore lint/plugin: RETIRED, do not copy. Call it through useSelectQuery/useSelectMutation when touching this.
      await getOrderItemsStatusSummary.handler({
        'advertiser-id': session.advertiser.id,
        search,
      }),
  })

  // Fetch order item summary
  const orderItemSummaryQuery = useQuery({
    queryKey: ['order-items-aggregated-summary', status, search, filters],
    queryFn: () =>
      // biome-ignore lint/plugin: RETIRED, do not copy. Call it through useSelectQuery/useSelectMutation when touching this.
      getOrderItemSummary.handler({
        status,
        search,
        ...filters,
      }),
  })

  return {
    statusSummary: statusSummaryQuery.data,
    orderItemSummary: orderItemSummaryQuery.data,
    isLoading: statusSummaryQuery.isLoading || orderItemSummaryQuery.isLoading,
    isError: statusSummaryQuery.isError || orderItemSummaryQuery.isError,
  }
}
